/**
 * UserVitals Component
 *
 * Description: This component manages the display of completed and pending biomarker vitals for a user.
 * It includes functionality to fetch, display, and update vitals data with dropdowns for detailed information.
 * The component handles expanding and collapsing vitals details for both completed and to-be-completed vitals.
 * Additionally, the component fetches the necessary data from the server and updates the vitals upon user interactions.
 *
 * File Name: UserVitals.tsx
 * Date: 24-08-2024
 */

import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import httpRequest from "src/helpers/httpRequest";
import {
  ICompletedVitals,
  IAddSingleBio,
  TabType,
} from "src/types/GlobalInterfaces";
import { CircularProgress } from "@mui/material";
import { useSelector } from "react-redux";
import { RootState } from "@store/store";
import CompletedVitalComponent from "src/Components/Customer/CompletedVitalComponent";
import PendingVitalComponent from "src/Components/Customer/PendingVitalComponent";
import TabsButton from "src/Components/AllTabs/TabsButton";
import TabsSlideContainer from "../Widget/TabsSlideContainer";
import { formateVitalData } from "src/helpers/helperFunctions";

type TabsType = "Vitals" | "Biomarkers";

const UserVitals: React.FC<{
  setActiveTab?: React.Dispatch<React.SetStateAction<TabsType>>;
  activeTab?: TabsType;
  onClose?: () => void;
}> = ({ activeTab, onClose, setActiveTab }) => {
  // id is user id
  const { userDetails } = useSelector((store: RootState) => store.appReducer); // Extracting user details from the Redux store

  // State for managing loading indicators
  const [completedLoader, setCompletedLoader] = useState<boolean>(false);
  const [pendingLoader, setPendingLoader] = useState<boolean>(false);

  const [completedList, setCompletedList] = useState<ICompletedVitals[]>([]); // State for storing the list of completed vitals

  const [toBeCompletedList, setToBeCompletedList] = useState<IAddSingleBio[]>(
    []
  ); // State for storing the list of to-be-completed

  /**
   * API_CALL: Fetch the completed vitals data from the server. Triggered when the component mounts.
   */
  const fetchCompletedVitals = async () => {
    setCompletedLoader(true);
    const { res, err } = await httpRequest<ICompletedVitals[]>({
      path: `/healthprofile/biomarker/details/${
        userDetails?._id
      }/completed?vital=${"true"}`,
    });
    if (res) {
      setCompletedList(res);
    } else {
      toast(err, { type: "error" });
    }
    setCompletedLoader(false);
  };

  /**
   * API_CALL: Fetch the to-be-completed vitals data from the server. Triggered when the component mounts.
   */
  const fetchToBeCompletedVitals = async () => {
    setPendingLoader(true);
    const { res, err } = await httpRequest<{
      message: string;
      data: { biomarkerData: IAddSingleBio[]; [key: string]: any };
    }>({
      path: `/healthprofile/biomarker/details/${
        userDetails?._id
      }?vital=${"true"}`,
    });
    if (res) {
      setToBeCompletedList(res?.data?.biomarkerData || []);
    } else {
      toast(err, { type: "error" });
    }
    setPendingLoader(false);
  };

  /**
   * Handles moving a vitals item from the "to be completed" list to the "completed" list.
   * Updates the state and triggers a refetch of completed vitals.
   */
  const addToVitals = (id: string) => {
    setToBeCompletedList((prev) => prev?.filter((v) => v?._id !== id)); //remove from toBeCompletedList;
    fetchCompletedVitals(); // completed List api again cal;
  };

  /**
   * Fetch the vitals data when the component mounts.
   */
  useEffect(() => {
    fetchCompletedVitals();
    fetchToBeCompletedVitals();
  }, []);

  let tabs: TabType[] = [
    {
      name: "Vitals",
      count: 0,
    },
    {
      name: "Biomarkers",
      count: 0,
    },
  ];

  return (
    <>
      <div>
        <div className="w-fit flex items-center gap-3 2xl:mb-2 mb-1.5">
          <TabsSlideContainer
            tabs={tabs}
            activeTab={tabs.find((tab) => tab.name === activeTab) as TabType}
            onChange={(tab: TabType) => {
              setActiveTab?.(tab.name as TabsType);
            }}
          />
        </div>
        <h2 className="theme-color-green-dark font-Poppins-Medium font-size-18px">
          Completed Vitals
        </h2>
        {completedLoader && (
          <div className="text-center mt-3">
            <CircularProgress
              style={{ width: 24, height: 24, color: "#00443f" }}
            />
          </div>
        )}
        <div className="health-vitals-row-main">
          {completedList.length > 0 ? (
            completedList
              .reverse()
              .map((item: ICompletedVitals, index: number) => (
                <CompletedVitalComponent
                  key={index}
                  item={item}
                  uid={userDetails?._id}
                  dob={userDetails?.dob || ""}
                  gender={userDetails?.gender || ""}
                  onSuccess={() => fetchCompletedVitals()}
                  index={index}
                />
              ))
          ) : (
            <p className="font-Poppins-Regular font-size-15px text-center">
              No Items
            </p>
          )}
        </div>
      </div>
      <hr className="products-tabs-hr-allOrder" />
      <div>
        <h2 className="theme-color-green-dark font-Poppins-Medium font-size-18px">
          To be Completed
        </h2>
        {pendingLoader && (
          <div className="text-center mt-3">
            <CircularProgress
              style={{ width: 24, height: 24, color: "#00443f" }}
            />
          </div>
        )}
        <div className="health-vitals-row-main">
          {toBeCompletedList.length > 0 ? (
            toBeCompletedList.map((item: IAddSingleBio, index: number) => (
              <PendingVitalComponent
                key={index}
                item={item}
                uid={userDetails?._id}
                dob={userDetails?.dob || userDetails?.date || ""}
                gender={userDetails?.gender || ""}
                onSuccess={addToVitals}
                index={index}
              />
            ))
          ) : (
            <p className="font-Poppins-Regular font-size-15px text-center">
              No Items
            </p>
          )}
        </div>
      </div>
    </>
  );
};

export default UserVitals;
